import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
} from '@angular/core';
import { AiAssistService, ScribeRow } from '../ai-assist.service';

declare var webkitSpeechRecognition: any;

type Phase = 'idle' | 'recording' | 'extracting' | 'review' | 'done' | 'error';

const SAMPLE_TRANSCRIPT =
  'this patient has a corneal infection in the right eye, start him on ' +
  'maxi flox eye drops four times a day for one week right eye, also add ' +
  'lubricating drops carboxymethylcellulose both eyes three times daily for a ' +
  'month, give pred acetate right eye taper over four weeks, and tablet ' +
  'paracetamol 500 twice a day for three days after food';

/**
 * AI Scribe — records the doctor's spoken consultation (Chrome Web Speech),
 * sends the transcript to the DeepSeek scribe, and shows an editable structured
 * table the doctor confirms before it fills the grid via (apply).
 */
@Component({
  selector: 'app-ai-scribe',
  templateUrl: './ai-scribe.component.html',
  styleUrls: ['./ai-scribe.component.scss'],
})
export class AiScribeComponent implements OnDestroy {
  @Input() section = 'medicine';
  @Input() mode: 'medicine' | 'workup' = 'medicine';
  @Input() title = 'AI Scribe';
  @Input() buttonLabel = 'Record Consultation';
  @Output() apply = new EventEmitter<any>();

  /** workup mode: the full extracted sections object + counts */
  workup: any = null;
  workupCounts: { [k: string]: number } = {};

  open = false;
  phase: Phase = 'idle';
  transcript = '';
  rows: ScribeRow[] = [];
  model = '';
  errorText = '';
  supported = true;
  appliedCount = 0;
  simulating = false;
  micAvailable = false;

  /** Sarvam STT (server-side) — capture audio, transcribe on stop. */
  transcribing = false;
  private useSarvam = true;
  private mediaRecorder: any = null;
  private mediaStream: any = null;
  private audioChunks: any[] = [];

  /** Demo scenarios — stream in like live dictation (no mic needed). */
  readonly scenarios = [
    {
      label: 'Cataract post-op',
      text:
        'post cataract surgery review right eye, eye is healing well, continue ' +
        'moxifloxacin drops four times a day for one more week right eye, ' +
        'prednisolone acetate taper over four weeks right eye, add lubricant ' +
        'drops both eyes three times a day for a month, review after one week',
    },
    {
      label: 'Conjunctivitis',
      text:
        'this child has viral conjunctivitis both eyes, start moxifloxacin ' +
        'drops three times a day for five days both eyes, lubricant drops as ' +
        'needed, and paracetamol 250 syrup twice a day for two days if fever',
    },
    {
      label: 'Glaucoma',
      text:
        'follow up glaucoma patient, pressure is slightly high in the left eye, ' +
        'start timolol drops twice a day left eye, continue latanoprost at ' +
        'night both eyes, review with pressures after two weeks',
    },
  ];

  /** Full-consultation demo scripts for workup mode. */
  readonly workupScenarios = [
    {
      label: 'Cataract — full consult',
      text:
        'this 62 year old diabetic patient complains of gradually decreasing blurred ' +
        'vision in the right eye for the last six months, with glare while driving at ' +
        'night for two months, no pain or redness. intraocular pressure is 16 in the ' +
        'right eye and 15 in the left eye. ' +
        // findings (both eyes, multiple)
        'on slit lamp examination, the right eye cornea is clear, anterior chamber is ' +
        'normal depth, the lens shows nuclear sclerosis grade 3 cataract, and the pupil ' +
        'is reacting normally. the right eye fundus is hazy due to the cataract. in the ' +
        'left eye the cornea is clear, the lens shows early immature cataract, and the ' +
        'fundus is normal with a healthy disc and macula. ' +
        // investigations (multiple)
        'order a B-scan, IOL master biometry, and keratometry for the right eye, an ' +
        'OCT of the macula, and check fasting blood sugar and HbA1c. ' +
        // diagnoses
        'the diagnosis is immature senile cataract in the right eye, primary, and early ' +
        'immature cataract in the left eye. ' +
        // surgery
        'advise phacoemulsification with foldable intraocular lens implantation in the ' +
        'right eye. ' +
        // medicines (multiple, with route/dose/duration/eye)
        'start moxifloxacin 0.5% eye drops four times a day for one week in the right ' +
        'eye, nepafenac eye drops three times a day for two weeks in the right eye, ' +
        'carboxymethylcellulose lubricant drops in both eyes three times a day for one ' +
        'month, and tablet paracetamol 500 twice a day for three days. ' +
        // review + other-info (prognosis, nutrition, management, referral, plan, remarks)
        'review after two weeks with the reports for surgical planning. the prognosis ' +
        'is very good after surgery. advise a low salt diabetic diet. the management ' +
        'plan is to control blood sugar below 150 before scheduling surgery. refer the ' +
        'patient to the physician for diabetic and anaesthetic fitness clearance. ' +
        'counsel the patient about premium and toric IOL options for better outcomes, ' +
        'and ask them to bring all the reports on the next visit.',
    },
    {
      label: 'Glaucoma — full consult',
      text:
        'this follow up glaucoma patient complains of occasional headache and mild ' +
        'blurring in the left eye for three weeks. intraocular pressure today is 18 in ' +
        'the right eye and 26 in the left eye. ' +
        'on examination the right optic disc cup to disc ratio is 0.4, the left optic ' +
        'disc shows an increased cup to disc ratio of 0.8 with thinning of the neuro ' +
        'retinal rim, both anterior chambers are quiet and the lenses are clear. ' +
        'order an OCT of the optic nerve head and RNFL, a Humphrey visual field test ' +
        'for both eyes, gonioscopy, and a central corneal thickness pachymetry. ' +
        'diagnosis is primary open angle glaucoma, advanced in the left eye and early ' +
        'in the right eye. advise trabeculectomy in the left eye if pressure is not ' +
        'controlled. start timolol 0.5% drops twice a day in the left eye, continue ' +
        'latanoprost drops at night in both eyes, and add brimonidine twice a day in ' +
        'the left eye. review after one month with repeat pressures. the prognosis is ' +
        'fair with strict treatment and good compliance. advise regular aerobic ' +
        'exercise and to avoid heavy weight lifting. the management plan is to target ' +
        'pressure below 16 and repeat fields in three months. refer to the low vision ' +
        'clinic for the left eye. counsel the patient strongly on the importance of ' +
        'using the drops daily and never missing follow up.',
    },
  ];

  get activeScenarios() {
    return this.mode === 'workup' ? this.workupScenarios : this.scenarios;
  }

  private simTimer: any = null;

  /** Cached review-panel summary — computed ONCE when the workup arrives, so the
   *  *ngFor never gets a fresh array on every change-detection pass (= flicker). */
  workupSummary: { label: string; count: number; sample: string }[] = [];

  private computeWorkupSummary(): { label: string; count: number; sample: string }[] {
    if (!this.workup) { return []; }
    const w = this.workup;
    const names = (arr: any[], keys: string[]) =>
      (arr || []).map((r) => keys.map((k) => r[k]).find((v) => v)).filter(Boolean).join(', ');
    const pe = w.primaryExam || {};
    const out = [
      { label: 'Complaints', count: (pe.complaints || []).length, sample: names(pe.complaints || [], ['complaint']) },
      { label: 'IOP', count: (pe.iop && (pe.iop.re || pe.iop.le)) ? 1 : 0,
        sample: pe.iop ? `RE ${pe.iop.re || '-'} / LE ${pe.iop.le || '-'} mmHg` : '' },
      { label: 'Findings', count: (w.findings || []).length, sample: names(w.findings, ['findingName']) },
      { label: 'Investigations', count: (w.investigations || []).length, sample: names(w.investigations, ['serviceName']) },
      { label: 'Diagnoses', count: (w.diagnoses || []).length, sample: names(w.diagnoses, ['diagnosisName']) },
      { label: 'Medicines', count: (w.medicines || []).length, sample: names(w.medicines, ['medicineName']) },
      { label: 'Surgeries', count: (w.surgeries || []).length, sample: names(w.surgeries, ['surgeryName']) },
      { label: 'Review', count: (w.review && (w.review.nextReview || w.review.nextReviewNote)) ? 1 : 0,
        sample: w.review ? [w.review.nextReview, w.review.nextReviewUnit].filter(Boolean).join(' ') : '' },
      { label: 'Other info', count: w.otherInfo && Object.keys(w.otherInfo).some((k) => w.otherInfo[k]) ? 1 : 0,
        sample: w.otherInfo ? [w.otherInfo.prognosis, w.otherInfo.nutritionalAdvice, w.otherInfo.nextVisitPlan].filter(Boolean).join('; ') : '' },
    ];
    return out.filter((s) => s.count > 0);
  }

  /** Conversational confirmation the agent "says" before inserting anything. */
  get confirmMessage(): string {
    if (this.mode === 'workup') {
      const parts = this.workupSummary.map((s) => `${s.count} ${s.label.toLowerCase()}`);
      if (!parts.length) { return 'I could not find any workup details in that consultation.'; }
      return (
        `I structured the consultation into: ${parts.join(', ')}.\n\n` +
        `Shall I fill these into the workup? Open each tab to review — ` +
        `nothing is saved until you click Save.`
      );
    }
    const n = this.rows.length;
    if (!n) { return ''; }
    const list = this.rows
      .map((r) => {
        const eye =
          r.site === 'RE' ? 'right eye' :
          r.site === 'LE' ? 'left eye' :
          r.site === 'BE' ? 'both eyes' : '';
        const unit = r.daysUnit === 'W' ? 'weeks' : r.daysUnit === 'M' ? 'months' : 'days';
        const dur = r.days ? `${r.days} ${unit}` : '';
        const bits = [eye, dur].filter(Boolean).join(', ');
        return `• ${r.medicineName}${bits ? ' (' + bits + ')' : ''}`;
      })
      .join('\n');
    return (
      `I heard the consultation and prepared ${n} medicine${n > 1 ? 's' : ''}:\n` +
      `${list}\n\nShall I add ${n > 1 ? 'them' : 'it'} to the prescription? ` +
      `You can edit any row first — nothing is saved until you click Save.`
    );
  }

  get doneMessage(): string {
    if (this.mode === 'workup') {
      return (
        '✓ The workup is filled across all sections as a draft. Open each tab ' +
        '(Findings, Investigations, Diagnosis, Medicine, Surgery, Review, Other info) ' +
        'to review, then Save.'
      );
    }
    return (
      `✓ Added ${this.appliedCount} medicine${this.appliedCount > 1 ? 's' : ''} ` +
      `to the prescription as a draft. Please review and click Save to record ${this.appliedCount > 1 ? 'them' : 'it'}.`
    );
  }

  private recog: any = null;

  constructor(private assist: AiAssistService, private zone: NgZone) {
    const md: any = navigator.mediaDevices;
    const canRecord = !!(md && md.getUserMedia && (window as any).MediaRecorder);
    this.supported =
      canRecord || typeof (window as any).webkitSpeechRecognition !== 'undefined';
  }

  ngOnDestroy(): void {
    this.cancelRecording();
    this.stopRecog();
    this.stopSim();
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) {
      this.checkMic();
    } else {
      this.stopRecog();
      this.stopSim();
    }
  }

  /** Detect whether a real microphone exists (false on a VM / mic-less box). */
  private checkMic(): void {
    try {
      const md: any = navigator.mediaDevices;
      if (!md || !md.enumerateDevices) { this.micAvailable = false; return; }
      md.enumerateDevices()
        .then((list: any[]) =>
          this.zone.run(
            () => (this.micAvailable = list.some((d) => d.kind === 'audioinput')),
          ),
        )
        .catch(() => this.zone.run(() => (this.micAvailable = false)));
    } catch {
      this.micAvailable = false;
    }
  }

  close(): void {
    this.cancelRecording();
    this.stopRecog();
    this.open = false;
  }

  reset(): void {
    this.cancelRecording();
    this.stopRecog();
    this.stopSim();
    this.phase = 'idle';
    this.transcript = '';
    this.rows = [];
    this.errorText = '';
    this.transcribing = false;
  }

  // ── Recording ───────────────────────────────────────────────────────────────
  // Primary: capture mic audio and transcribe via the server-side Sarvam proxy
  // (Indian languages + code-switching). Fallback: Chrome Web Speech.
  startRecording(): void {
    // Mic only works on a secure origin (localhost or https)
    const host = window.location.hostname;
    const secureOrigin =
      window.location.protocol === 'https:' || host === 'localhost' || host === '127.0.0.1';
    if (!secureOrigin) {
      this.errorText =
        `Microphone is blocked because the page is opened as "${host}". ` +
        'Open the app over HTTPS (or http://localhost:4200). ' +
        'For now, click "Use sample" to demo without a mic.';
      this.phase = 'error';
      return;
    }
    const md: any = navigator.mediaDevices;
    const canRecord = !!(md && md.getUserMedia && (window as any).MediaRecorder);
    if (this.useSarvam && canRecord) {
      this.startSarvamRecording();
    } else if (typeof (window as any).webkitSpeechRecognition !== 'undefined') {
      this.startWebSpeech();
    } else {
      this.errorText = 'Voice capture is not supported here. Click "Use sample" to demo.';
      this.phase = 'error';
    }
  }

  /** Record mic audio; on stop, send it to the Sarvam STT proxy. */
  private startSarvamRecording(): void {
    this.transcript = '';
    this.rows = [];
    this.errorText = '';
    const md: any = navigator.mediaDevices;
    md.getUserMedia({ audio: true })
      .then((stream: any) => {
        this.mediaStream = stream;
        this.audioChunks = [];
        const mr = new (window as any).MediaRecorder(stream);
        mr.ondataavailable = (e: any) => {
          if (e.data && e.data.size > 0) this.audioChunks.push(e.data);
        };
        mr.onstop = () => this.onSarvamStop();
        this.mediaRecorder = mr;
        mr.start();
        this.zone.run(() => (this.phase = 'recording'));
      })
      .catch(() =>
        this.zone.run(() => {
          this.errorText =
            'Microphone access is blocked. Allow the mic in your browser (lock ' +
            'icon in the address bar), or click "Use sample" to demo without one.';
          this.phase = 'error';
        }),
      );
  }

  private onSarvamStop(): void {
    const type = (this.mediaRecorder && this.mediaRecorder.mimeType) || 'audio/webm';
    const recorded = new Blob(this.audioChunks, { type });
    this.releaseStream();
    if (!recorded.size) {
      this.zone.run(() => (this.phase = 'idle'));
      return;
    }
    this.zone.run(() => {
      this.phase = 'extracting';
      this.transcribing = true;
    });
    this.blobToWav(recorded)
      .then((wav) => {
        this.assist.scribeTranscribe(wav).subscribe({
          next: (res: any) =>
            this.zone.run(() => {
              this.transcribing = false;
              this.transcript = ((res && res.transcript) || '').trim();
              if (!this.transcript) {
                this.errorText =
                  (res && res.error) || 'No speech detected. Please try again or use sample.';
                this.phase = 'error';
                return;
              }
              this.extract();
            }),
          error: () =>
            this.zone.run(() => {
              this.transcribing = false;
              this.errorText =
                'Could not reach the transcription service. Is the backend running on :8000?';
              this.phase = 'error';
            }),
        });
      })
      .catch(() =>
        this.zone.run(() => {
          this.transcribing = false;
          this.errorText = 'Could not process the recording. Please try again or use sample.';
          this.phase = 'error';
        }),
      );
  }

  // ── Fallback: Chrome Web Speech ─────────────────────────────────────────────
  private startWebSpeech(): void {
    this.transcript = '';
    this.rows = [];
    this.phase = 'recording';

    const r = new (window as any).webkitSpeechRecognition();
    r.lang = 'en-IN';
    r.continuous = true;
    r.interimResults = true;

    let finalText = '';
    r.onresult = (e: any) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t + ' ';
        else interim += t;
      }
      this.zone.run(() => (this.transcript = (finalText + interim).trim()));
    };
    r.onerror = (e: any) => {
      this.zone.run(() => {
        if (e.error === 'no-speech' || e.error === 'aborted') { return; }
        this.errorText = 'Microphone error: ' + e.error + '. Try "Use sample".';
        this.phase = 'error';
      });
    };
    r.onend = () => {
      if (this.phase === 'recording' && this.recog) {
        try { r.start(); } catch { /* ignore */ }
      }
    };
    this.recog = r;
    try { r.start(); } catch { /* ignore double-start */ }
  }

  stopRecording(): void {
    this.stopSim();
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try { this.mediaRecorder.stop(); } catch { /* ignore */ }
      return; // onSarvamStop() handles transcription + extraction
    }
    this.stopRecog();
    if (this.transcript.trim()) {
      this.extract();
    } else {
      this.phase = 'idle';
    }
  }

  /** Stop recording WITHOUT transcribing (teardown on close/destroy). */
  private cancelRecording(): void {
    if (this.mediaRecorder) {
      try {
        this.mediaRecorder.onstop = null;
        if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
      } catch { /* ignore */ }
    }
    this.releaseStream();
  }

  private releaseStream(): void {
    if (this.mediaStream) {
      try { this.mediaStream.getTracks().forEach((t: any) => t.stop()); } catch { /* ignore */ }
      this.mediaStream = null;
    }
    this.mediaRecorder = null;
    this.audioChunks = [];
  }

  // ── recorded audio (webm/opus or mp4) -> 16 kHz mono WAV for Sarvam ──────────
  private async blobToWav(blob: Blob): Promise<Blob> {
    const arrayBuf = await blob.arrayBuffer();
    const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
    let ctx: any;
    try { ctx = new AC({ sampleRate: 16000 }); } catch { ctx = new AC(); }
    const audioBuf: AudioBuffer = await ctx.decodeAudioData(arrayBuf);
    let samples: Float32Array = audioBuf.getChannelData(0);
    let sr = audioBuf.sampleRate;
    if (sr !== 16000) { samples = this.downsample(samples, sr, 16000); sr = 16000; }
    try { ctx.close(); } catch { /* ignore */ }
    return this.encodeWav(samples, sr);
  }

  private downsample(buf: Float32Array, from: number, to: number): Float32Array {
    if (to >= from) return buf;
    const ratio = from / to;
    const len = Math.round(buf.length / ratio);
    const out = new Float32Array(len);
    let oi = 0, bi = 0;
    while (oi < len) {
      const next = Math.round((oi + 1) * ratio);
      let sum = 0, c = 0;
      for (let i = bi; i < next && i < buf.length; i++) { sum += buf[i]; c++; }
      out[oi] = c ? sum / c : 0;
      oi++; bi = next;
    }
    return out;
  }

  private encodeWav(samples: Float32Array, sampleRate: number): Blob {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    const writeStr = (o: number, s: string) => {
      for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i));
    };
    writeStr(0, 'RIFF'); view.setUint32(4, 36 + samples.length * 2, true); writeStr(8, 'WAVE');
    writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
    view.setUint16(22, 1, true); view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    writeStr(36, 'data'); view.setUint32(40, samples.length * 2, true);
    let o = 44;
    for (let i = 0; i < samples.length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      o += 2;
    }
    return new Blob([view], { type: 'audio/wav' });
  }

  useSample(): void {
    this.stopRecog();
    this.transcript = SAMPLE_TRANSCRIPT;
    this.extract();
  }

  /** Street-smart demo: stream a scenario in word-by-word like live STT. */
  simulate(idx: number): void {
    this.stopRecog();
    this.stopSim();
    const words = (this.activeScenarios[idx]?.text || '').split(' ');
    this.transcript = '';
    this.rows = [];
    this.errorText = '';
    this.phase = 'recording';
    this.simulating = true;
    let i = 0;
    this.simTimer = setInterval(() => {
      this.transcript = (this.transcript + ' ' + words[i]).trim();
      i++;
      if (i >= words.length) {
        this.stopSim();
        setTimeout(() => this.extract(), 550);
      }
    }, 95);
  }

  private stopSim(): void {
    if (this.simTimer) {
      clearInterval(this.simTimer);
      this.simTimer = null;
    }
    this.simulating = false;
  }

  private stopRecog(): void {
    if (this.recog) {
      const r = this.recog;
      this.recog = null;
      try { r.onend = null; r.stop(); } catch { /* ignore */ }
    }
  }

  // ── Extraction ──────────────────────────────────────────────────────────────
  extract(): void {
    const t = this.transcript.trim();
    if (!t) return;
    this.phase = 'extracting';

    if (this.mode === 'workup') {
      this.assist.scribeWorkup(t).subscribe({
        next: (res) => {
          this.model = res.model;
          this.workup = res.sections || {};
          this.workupCounts = res.counts || {};
          this.workupSummary = this.computeWorkupSummary();
          const total = Object.keys(this.workupCounts)
            .reduce((a, k) => a + (+this.workupCounts[k] || 0), 0);
          if (!res.ok || !total) {
            this.errorText = res.error || 'Nothing detected in the transcript.';
            this.phase = total ? 'review' : 'error';
          } else {
            this.phase = 'review';
          }
        },
        error: () => {
          this.errorText = 'Could not reach the AI scribe. Is the backend running on :8000?';
          this.phase = 'error';
        },
      });
      return;
    }

    this.assist.scribe(this.section, t).subscribe({
      next: (res) => {
        this.model = res.model;
        this.rows = res.rows || [];
        if (!res.ok || !this.rows.length) {
          this.errorText = res.error || 'No prescription detected in the transcript.';
          this.phase = this.rows.length ? 'review' : 'error';
        } else {
          this.phase = 'review';
        }
      },
      error: () => {
        this.errorText = 'Could not reach the AI scribe. Is the backend running on :8000?';
        this.phase = 'error';
      },
    });
  }

  removeRow(i: number): void {
    this.rows.splice(i, 1);
  }

  applyRows(): void {
    if (this.mode === 'workup') {
      if (!this.workup) { return; }
      this.apply.emit(this.workup);
      this.phase = 'done';
      return;
    }
    if (!this.rows.length) { return; }
    this.appliedCount = this.rows.length;
    this.apply.emit(this.rows.slice());
    this.phase = 'done';
  }

  finishDone(): void {
    this.close();
    this.phase = 'idle';
    this.transcript = '';
    this.rows = [];
  }
}
